import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoAdd } from "react-icons/io5";
import { resolvePageMeta, isSectionPage } from "../topbar/pageMeta";
import "./blockTitle.css";

/**
 * Заголовок страницы с кнопками действий.
 *
 * По умолчанию кнопки рендерятся в верхнюю панель приложения
 * (#topbar-actions) в новом стиле — так на странице не остаётся старой «шапки».
 *
 * Внутри карточки пациента (вложенные экраны: УЗИ-протокол, документ медкарты,
 * план лечения и т.п.) кнопки остаются в контенте — отдельной панелью под
 * заголовком, чтобы было видно, к какому объекту они относятся.
 *
 * buttons: [{ label, onClick, type: "normal" | "light" | "danger" }]
 * inline: true | false — принудительно включить/выключить режим «в контенте»
 */
// Кнопки добавления/создания получают аккуратный значок «плюс»
const ADD_LABEL_RE = /(добав|созда|create|add|new|нов|إضافة|إنشاء)/i;

// Вложенная сущность внутри карточки пациента: /patient/:id/<section>/<id>
const isPatientNestedPath = (pathname) => {
    const parts = String(pathname || "")
        .split("/")
        .filter(Boolean);
    return parts[0] === "patient" && parts.length >= 4;
};

const getTitleText = (title) => {
    if (React.isValidElement(title)) {
        return title.props?.title ?? "";
    }
    if (typeof title === "string" || typeof title === "number") {
        return String(title);
    }
    return "";
};

// Текст внутри кнопки (метка может быть JSX: иконка + текст)
const getLabelText = (label) => {
    if (typeof label === "string" || typeof label === "number") {
        return String(label).trim();
    }
    if (React.isValidElement(label)) {
        return React.Children.toArray(label.props?.children)
            .filter(
                (child) =>
                    typeof child === "string" || typeof child === "number",
            )
            .join(" ")
            .trim();
    }
    return "";
};

// Иконки внутри кнопки (если метка — JSX с svg)
const getLabelIcons = (label) => {
    if (!React.isValidElement(label)) return null;
    const icons = React.Children.toArray(label.props?.children).filter(
        React.isValidElement,
    );
    return icons.length > 0 ? icons : null;
};

// Печать / удаление / возврат к списку остаются только иконками (с подсказкой)
const ICON_ONLY_RE = /(печат|print|طباعة|удал|delete|حذف|верну|back|رجوع|назад)/i;

/**
 * Кнопка действия внутри контента: компактная, при наличии иконки —
 * либо «только иконка» (печать, удалить, вернуться), либо мелкий текст
 * (редактировать и т.п.).
 */
const renderContentAction = (button, index) => {
    const type = button?.type || "normal";
    const labelText = getLabelText(button?.label);
    const icons = getLabelIcons(button?.label);
    const iconOnly = Boolean(icons) && ICON_ONLY_RE.test(labelText);

    if (iconOnly) {
        return (
            <button
                key={`${labelText}-${index}`}
                type="button"
                className={`topbar-action topbar-action--${type} block-action-icon`}
                onClick={button?.onClick}
                title={labelText}
                aria-label={labelText}>
                {icons}
            </button>
        );
    }

    // Текстовые кнопки: иконку убираем, оставляем компактный текст
    const content =
        icons && labelText ? (
            <span className="block-action-text">{labelText}</span>
        ) : (
            button?.label
        );

    return (
        <button
            key={`${labelText}-${index}`}
            type="button"
            className={`topbar-action topbar-action--${type} block-action-compact`}
            onClick={button?.onClick}>
            {content}
        </button>
    );
};

const BlockTitle = ({ title, buttons = [], showTitle, inline }) => {
    const { t } = useTranslation("menu");
    const { pathname } = useLocation();
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.getElementById("topbar-actions"));
    }, []);

    const inContent =
        typeof inline === "boolean" ? inline : isPatientNestedPath(pathname);

    const meta = resolvePageMeta(pathname);
    const pageTitle = meta ? String(t(meta.title) || "").trim() : "";
    const titleText = getTitleText(title).trim();

    const isDuplicate = Boolean(pageTitle) && titleText === pageTitle;
    const shouldShowTitle =
        showTitle === true
            ? Boolean(title)
            : showTitle === false
              ? false
              : Boolean(title) &&
                Boolean(titleText) &&
                !isDuplicate &&
                !isSectionPage(pathname);

    const actions = (Array.isArray(buttons) ? buttons : []).map(
        (button, index) => {
            const type = button?.type || "normal";
            const labelText =
                typeof button?.label === "string" ? button.label : "";
            const withPlus = ADD_LABEL_RE.test(labelText);

            return (
                <button
                    key={`${labelText}-${index}`}
                    type="button"
                    className={`topbar-action topbar-action--${type}`}
                    onClick={button?.onClick}>
                    {withPlus && (
                        <span
                            className="topbar-action__icon"
                            aria-hidden="true">
                            <IoAdd size={16} />
                        </span>
                    )}
                    <span className="topbar-action__label">
                        {button?.label}
                    </span>
                </button>
            );
        },
    );

    if (!shouldShowTitle && actions.length === 0) {
        return null;
    }

    return (
        <>
            {!inContent && host && actions.length > 0
                ? createPortal(actions, host)
                : null}
            {shouldShowTitle ? (
                <div className="block-title">
                    <div className="block-title-text">{title}</div>
                </div>
            ) : null}
            {inContent && actions.length > 0 ? (
                <div className="block-actions">
                    {(Array.isArray(buttons) ? buttons : []).map(
                        renderContentAction,
                    )}
                </div>
            ) : null}
        </>
    );
};

export default BlockTitle;
